// CreatorEditor.jsx - FIXED VERSION with proper ID handling
import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './CreatorEditor.css';

const AUTHORIZED_USER_ID = 'user_32WwQhUfF1SC1qNOFDyEkAzklkh';

export default function CreatorEditor() {
  const { user, isLoaded } = useUser();
  const navigate = useNavigate();
  
  // Security check - wait for Clerk to load first
  useEffect(() => {
    if (!isLoaded) return;
    
    if (!user || user.id !== AUTHORIZED_USER_ID) {
      navigate('/');
    }
  }, [user, isLoaded, navigate]);

  const [streams, setStreams] = useState({ upcoming: [], recent: [] });
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showPreview, setShowPreview] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [loading, setLoading] = useState(true);

  // Load streams from Supabase
  const loadStreams = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('streams')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Organize streams by type
      const organized = {
        upcoming: data?.filter(s => s.stream_type === 'upcoming') || [],
        recent: data?.filter(s => s.stream_type === 'recent') || []
      };

      setStreams(organized);
      console.log('Loaded streams from Supabase:', organized);
    } catch (error) {
      console.error('Error loading streams:', error);
      setSaveStatus('❌ Error loading streams');
    } finally {
      setLoading(false);
    }
  };

  // Load streams on component mount
  useEffect(() => {
    if (user && user.id === AUTHORIZED_USER_ID) {
      loadStreams();
    }
  }, [user]);

  // Save streams to Supabase - FIXED VERSION
  const saveStreams = async () => {
    setSaveStatus('⏳ Saving...');
    
    try {
      // First, delete all existing streams (we'll replace them)
      const { error: deleteError } = await supabase
        .from('streams')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (using impossible ID)

      if (deleteError && deleteError.code !== 'PGRST116') {
        throw deleteError;
      }

      // Prepare all streams for insertion
      const allStreams = [];
      
      // Add upcoming streams - CRITICAL: Don't include 'id' field, database generates UUIDs
      streams.upcoming.forEach((stream, index) => {
        allStreams.push({
          stream_type: 'upcoming',
          title: stream.title,
          date_time: stream.date || stream.date_time || new Date().toISOString(),
          thumbnail: stream.thumbnail || '/streams/default.jpg',
          youtube_url: stream.youtubeUrl || stream.youtube_url || '',
          description: stream.description || '',
          status: stream.status || 'upcoming',
          display_order: index
        });
      });

      // Add recent streams - CRITICAL: Don't include 'id' field, database generates UUIDs
      streams.recent.forEach((stream, index) => {
        allStreams.push({
          stream_type: 'recent',
          title: stream.title,
          date_time: stream.date || stream.date_time || new Date().toISOString(),
          thumbnail: stream.thumbnail || '/streams/default.jpg',
          youtube_url: stream.youtubeUrl || stream.youtube_url || '',
          view_count: stream.viewCount || stream.view_count || '0 views',
          highlights: stream.highlights || '',
          display_order: index
        });
      });

      // Insert all streams
      if (allStreams.length > 0) {
        const { error: insertError } = await supabase
          .from('streams')
          .insert(allStreams);

        if (insertError) {
          console.error('Insert error details:', insertError);
          throw insertError;
        }
      }

      setSaveStatus('✅ Saved successfully to database!');
      
      // IMPORTANT: Reload streams from database to get proper UUIDs
      await loadStreams();
      
      setTimeout(() => setSaveStatus(''), 3000);
      
    } catch (error) {
      console.error('Error saving streams:', error);
      setSaveStatus(`❌ Error: ${error.message}`);
      setTimeout(() => setSaveStatus(''), 5000);
    }
  };

  // Add new stream - Uses numeric timestamp for local ID (won't be sent to DB)
  const addStream = (type) => {
    const newStream = type === 'upcoming' ? {
      id: Date.now(), // Local ID only, database will generate UUID
      title: "🔴 New Stream Title",
      date: "Day, Month Date @ Time EST",
      thumbnail: "/streams/default.jpg",
      youtubeUrl: "https://youtube.com/live/",
      status: "upcoming",
      description: "Stream description here"
    } : {
      id: Date.now(), // Local ID only, database will generate UUID
      title: "New Past Stream",
      date: new Date().toLocaleDateString(),
      thumbnail: "/streams/default.jpg",
      youtubeUrl: "https://youtube.com/watch?v=",
      viewCount: "0 views",
      highlights: "Key highlights here"
    };

    setStreams(prev => ({
      ...prev,
      [type]: [...prev[type], newStream]
    }));
  };

  // Delete stream
  const deleteStream = (type, id) => {
    if (confirm('Are you sure you want to delete this stream?')) {
      setStreams(prev => ({
        ...prev,
        [type]: prev[type].filter(s => s.id !== id)
      }));
    }
  };

  // Update stream
  const updateStream = (type, id, updates) => {
    setStreams(prev => ({
      ...prev,
      [type]: prev[type].map(s => s.id === id ? { ...s, ...updates } : s)
    }));
  };

  // Move stream between upcoming and recent
  const moveStream = (from, to, id) => {
    const stream = streams[from].find(s => s.id === id);
    if (stream) {
      const movedStream = { ...stream, id: Date.now() }; // New local ID
      if (to === 'recent') {
        delete movedStream.description;
        movedStream.viewCount = "0 views";
        movedStream.highlights = "Stream completed!";
        movedStream.date = new Date().toLocaleDateString();
      } else {
        delete movedStream.viewCount;
        delete movedStream.highlights;
        movedStream.description = "Upcoming stream";
        movedStream.status = "upcoming";
      }
      
      setStreams(prev => ({
        [from]: prev[from].filter(s => s.id !== id),
        [to]: [...prev[to], movedStream]
      }));
    }
  };

  // Handle thumbnail upload
  const handleThumbnailUpload = (streamId, type, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateStream(type, streamId, { thumbnail: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Quick templates
  const applyTemplate = (type, streamId, template) => {
    const templates = {
      friday: {
        title: "🔴 LIVE: Frozzlers Friday Night Fights - Tournament #",
        description: "Weekly tournament - Join with code: FRIDAY",
        date: "Friday @ 8PM EST"
      },
      tuesday: {
        title: "Code & Collision Tuesday - Building New Features",
        description: "Watch me code, then test it LIVE with viewers!",
        date: "Tuesday @ 7PM EST"
      },
      challenge: {
        title: "Beat the Dev Challenge - Can YOU Win?",
        description: "1v1 me for glory and feature requests!",
        date: "Sunday @ 3PM EST"
      }
    };
    
    if (templates[template]) {
      updateStream(type, streamId, templates[template]);
    }
  };

  // Show loading state while Clerk is loading
  if (!isLoaded) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e293b, #0f172a)',
        color: '#94a3b8'
      }}>
        <div>Checking authorization...</div>
      </div>
    );
  }

  // Redirect if not authorized
  if (!user || user.id !== AUTHORIZED_USER_ID) {
    return null;
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e293b, #0f172a)',
        color: '#94a3b8'
      }}>
        <div>Loading streams from database...</div>
      </div>
    );
  }

  return (
    <div className="creator-editor">
      <div className="editor-header">
        <h1>🎬 Stream Manager</h1>
        <div className="header-actions">
          <button 
            className="btn-preview"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? '✏️ Edit Mode' : '👁 Preview'}
          </button>
          <button 
            className="btn-save"
            onClick={saveStreams}
          >
            💾 Save to Database
          </button>
          {saveStatus && <span className="save-status">{saveStatus}</span>}
          <button 
            className="btn-back"
            onClick={() => navigate('/')}
          >
            ← Back to Site
          </button>
        </div>
      </div>

      {showPreview ? (
        // Preview Mode - shows how it will look on landing page
        <div className="preview-mode">
          <h2>Preview Mode - This is how it will appear on the landing page:</h2>
          <div className="streams-preview">
            {/* Upcoming Streams Preview */}
            <div className="upcoming-preview">
              <h3>🔴 Upcoming Streams</h3>
              <div className="preview-grid">
                {streams.upcoming.map(stream => (
                  <div key={stream.id} className="preview-card">
                    <div className="preview-thumbnail">
                      {stream.thumbnail && stream.thumbnail.startsWith('data:') ? (
                        <img src={stream.thumbnail} alt={stream.title} />
                      ) : (
                        <div className="thumbnail-placeholder">🎮</div>
                      )}
                      <span className="preview-badge">UPCOMING</span>
                    </div>
                    <div className="preview-info">
                      <h4>{stream.title}</h4>
                      <p className="preview-date">📅 {stream.date || stream.date_time}</p>
                      <p className="preview-desc">{stream.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Streams Preview */}
            <div className="recent-preview">
              <h3>📹 Recent Streams & Highlights</h3>
              <div className="preview-grid">
                {streams.recent.map(stream => (
                  <div key={stream.id} className="preview-card">
                    <div className="preview-thumbnail">
                      {stream.thumbnail && stream.thumbnail.startsWith('data:') ? (
                        <img src={stream.thumbnail} alt={stream.title} />
                      ) : (
                        <div className="thumbnail-placeholder">🎬</div>
                      )}
                      <span className="view-count">{stream.viewCount || stream.view_count}</span>
                    </div>
                    <div className="preview-info">
                      <h4>{stream.title}</h4>
                      <p className="preview-date">{stream.date || stream.date_time}</p>
                      <p className="preview-highlight">✨ {stream.highlights}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Edit Mode
        <div className="edit-mode">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
              onClick={() => setActiveTab('upcoming')}
            >
              🔴 Upcoming Streams ({streams.upcoming.length})
            </button>
            <button 
              className={`tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              📹 Recent Streams ({streams.recent.length})
            </button>
          </div>

          <div className="streams-list">
            <div className="add-stream-section">
              <button 
                className="btn-add"
                onClick={() => addStream(activeTab)}
              >
                ➕ Add New {activeTab === 'upcoming' ? 'Upcoming' : 'Recent'} Stream
              </button>
              
              {activeTab === 'upcoming' && (
                <div className="quick-templates">
                  <span>Quick Templates:</span>
                  <button onClick={() => {
                    addStream('upcoming');
                    setTimeout(() => {
                      const lastId = Math.max(...streams.upcoming.map(s => s.id || 0));
                      applyTemplate('upcoming', lastId, 'friday');
                    }, 100);
                  }}>🏆 Friday Fights</button>
                  <button onClick={() => {
                    addStream('upcoming');
                    setTimeout(() => {
                      const lastId = Math.max(...streams.upcoming.map(s => s.id || 0));
                      applyTemplate('upcoming', lastId, 'tuesday');
                    }, 100);
                  }}>💻 Code Tuesday</button>
                  <button onClick={() => {
                    addStream('upcoming');
                    setTimeout(() => {
                      const lastId = Math.max(...streams.upcoming.map(s => s.id || 0));
                      applyTemplate('upcoming', lastId, 'challenge');
                    }, 100);
                  }}>⚔️ Beat the Dev</button>
                </div>
              )}
            </div>

            {streams[activeTab].map((stream, index) => (
              <div key={stream.id || index} className="stream-editor-card">
                <div className="card-header">
                  <span className="stream-number">#{index + 1}</span>
                  <div className="card-actions">
                    <button 
                      className="btn-move"
                      onClick={() => moveStream(activeTab, activeTab === 'upcoming' ? 'recent' : 'upcoming', stream.id)}
                      title={`Move to ${activeTab === 'upcoming' ? 'Recent' : 'Upcoming'}`}
                    >
                      {activeTab === 'upcoming' ? '📹' : '🔴'}
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => deleteStream(activeTab, stream.id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="editor-grid">
                  {/* Thumbnail Section */}
                  <div className="thumbnail-section">
                    <label>Thumbnail:</label>
                    <div className="thumbnail-preview">
                      {stream.thumbnail && stream.thumbnail.startsWith('data:') ? (
                        <img src={stream.thumbnail} alt="Thumbnail" />
                      ) : (
                        <div className="no-thumbnail">
                          <span>📷</span>
                          <p>No thumbnail</p>
                        </div>
                      )}
                    </div>
                    <input 
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleThumbnailUpload(stream.id, activeTab, e)}
                      id={`thumb-${stream.id || index}`}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor={`thumb-${stream.id || index}`} className="btn-upload">
                      📤 Upload Image
                    </label>
                    <input 
                      type="text"
                      placeholder="Or paste image URL"
                      value={stream.thumbnail && !stream.thumbnail.startsWith('data:') ? stream.thumbnail : ''}
                      onChange={(e) => updateStream(activeTab, stream.id, { thumbnail: e.target.value })}
                      className="url-input"
                    />
                  </div>

                  {/* Details Section */}
                  <div className="details-section">
                    <div className="input-group">
                      <label>Title:</label>
                      <input 
                        type="text"
                        value={stream.title}
                        onChange={(e) => updateStream(activeTab, stream.id, { title: e.target.value })}
                        className="input-title"
                      />
                    </div>

                    <div className="input-group">
                      <label>Date/Time:</label>
                      <input 
                        type="text"
                        value={stream.date || stream.date_time || ''}
                        onChange={(e) => updateStream(activeTab, stream.id, { date: e.target.value })}
                        className="input-date"
                      />
                    </div>

                    <div className="input-group">
                      <label>YouTube URL:</label>
                      <input 
                        type="text"
                        value={stream.youtubeUrl || stream.youtube_url || ''}
                        onChange={(e) => updateStream(activeTab, stream.id, { youtubeUrl: e.target.value })}
                        className="input-url"
                      />
                    </div>

                    {activeTab === 'upcoming' ? (
                      <div className="input-group">
                        <label>Description:</label>
                        <textarea 
                          value={stream.description || ''}
                          onChange={(e) => updateStream(activeTab, stream.id, { description: e.target.value })}
                          className="input-description"
                          rows="2"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="input-group">
                          <label>View Count:</label>
                          <input 
                            type="text"
                            value={stream.viewCount || stream.view_count || ''}
                            onChange={(e) => updateStream(activeTab, stream.id, { viewCount: e.target.value })}
                            className="input-views"
                          />
                        </div>
                        <div className="input-group">
                          <label>Highlights:</label>
                          <input 
                            type="text"
                            value={stream.highlights || ''}
                            onChange={(e) => updateStream(activeTab, stream.id, { highlights: e.target.value })}
                            className="input-highlights"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {streams[activeTab].length === 0 && (
              <div className="empty-state">
                <p>No {activeTab} streams yet.</p>
                <p>Click "Add New" to get started!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="editor-footer">
        <div className="stats">
          <span>📊 Total Streams: {streams.upcoming.length + streams.recent.length}</span>
          <span>🔴 Upcoming: {streams.upcoming.length}</span>
          <span>📹 Recent: {streams.recent.length}</span>
        </div>
        <div className="last-saved">
          Connected to Supabase Database
        </div>
      </div>
    </div>
  );
}
